import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertCircle,
  Award,
  BookOpen,
  CheckCircle2,
  Loader2,
  Sparkles,
  Target,
} from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { resumeAnalysisAPI } from '@/services/api';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface Improvement {
  area: string;
  severity: 'high' | 'medium' | 'low';
  suggestion: string;
}

interface RecommendedSkill {
  skill: string;
  reason: string;
  priority: 'high' | 'medium' | 'low';
}

interface RecommendedCertification {
  name: string;
  provider?: string;
  reason: string;
}

interface JdGap {
  matchScore: number;
  matchedRequirements: string[];
  missingRequirements: string[];
  gapSummary: string;
  tailoringSuggestions: string[];
}

interface ResumeAnalysis {
  analyzedAt: string;
  atsScore: number;
  atsSummary: string;
  formattingIssues: string[];
  keywordSuggestions: string[];
  improvements: Improvement[];
  recommendedSkills: RecommendedSkill[];
  recommendedCertifications: RecommendedCertification[];
  jdGap: JdGap | null;
}

interface Props {
  hasResume: boolean;
  isApproved: boolean;
}

const severityColor: Record<string, string> = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-blue-100 text-blue-800',
};

const priorityColor: Record<string, string> = {
  high: 'bg-primary-light text-green-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-slate-100 text-slate-700',
};

const ScoreRing: React.FC<{ score: number; label: string }> = ({ score, label }) => {
  const color =
    score >= 75 ? 'text-green-600' : score >= 50 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="flex flex-col items-center p-4 bg-white rounded-xl border border-slate-200 min-w-[120px]">
      <span className={`text-4xl font-bold ${color}`}>{score}</span>
      <span className="text-xs text-slate-500 mt-1 text-center">{label}</span>
    </div>
  );
};

const ResumeInsightsPanel: React.FC<Props> = ({ hasResume, isApproved }) => {
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [jdText, setJdText] = useState('');
  const [analysis, setAnalysis] = useState<ResumeAnalysis | null>(null);
  const [isStale, setIsStale] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [dailyRemaining, setDailyRemaining] = useState<number | null>(null);
  const { success, error } = useToast();

  const fetchStatus = async () => {
    try {
      const response = await resumeAnalysisAPI.getStatus();
      if (response.success) {
        setEnabled(response.data.enabled);
        setAnalysis(response.data.analysis);
        setIsStale(response.data.isStale);
        setDailyRemaining(response.data.dailyRemaining);
      }
    } catch (err) {
      console.error('Failed to fetch resume analysis status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [hasResume]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await resumeAnalysisAPI.analyze(jdText.trim() || undefined);
      if (response.success) {
        setAnalysis(response.data.analysis);
        setIsStale(false);
        setDailyRemaining(response.data.dailyRemaining);
        success('Resume analysis complete!');
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to analyze resume';
      error(message);
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="py-8 flex justify-center">
          <LoadingSpinner size="sm" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-orange-200 shadow-sm bg-gradient-to-br from-orange-50/40 to-white">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-orange-500" />
          Resume AI Insights
        </CardTitle>
        <CardDescription className="leading-relaxed">
          Get ATS score, improvement tips, skills and certification suggestions, and optional job
          description gap analysis.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!enabled && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
            Resume analysis is temporarily unavailable.
          </div>
        )}

        {!hasResume && (
          <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
            Upload your resume above to run AI analysis.
          </div>
        )}

        {hasResume && !isApproved && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            Your account must be approved by an admin before you can analyze your resume.
          </div>
        )}

        {isStale && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            Your resume changed since the last analysis. Run analysis again for updated feedback.
          </div>
        )}

        {hasResume && isApproved && enabled && (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Job description (optional)
              </label>
              <Textarea
                value={jdText}
                onChange={(e) => setJdText(e.target.value)}
                placeholder="Paste a job description here to see how your resume matches and what to improve..."
                className="min-h-[100px] text-sm"
                maxLength={8000}
              />
              <p className="text-xs text-slate-500 mt-1">
                Leave empty for ATS and general improvement feedback only.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={handleAnalyze}
                disabled={analyzing}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyze resume
                  </>
                )}
              </Button>
              {dailyRemaining !== null && (
                <span className="text-xs text-slate-500">
                  {dailyRemaining} analysis{dailyRemaining === 1 ? '' : 'es'} remaining today
                </span>
              )}
            </div>
          </>
        )}

        {analysis && (
          <div className="space-y-5 pt-2 border-t border-orange-100">
            <div className="flex flex-wrap gap-4 items-start">
              <ScoreRing score={analysis.atsScore} label="ATS Score" />
              {analysis.jdGap && (
                <ScoreRing score={analysis.jdGap.matchScore} label="JD Match" />
              )}
              {analysis.analyzedAt && (
                <div className="text-xs text-slate-500 self-end pb-2">
                  Analyzed {new Date(analysis.analyzedAt).toLocaleString()}
                </div>
              )}
            </div>

            <div className="p-4 bg-white rounded-lg border border-slate-200">
              <h4 className="text-sm font-semibold text-slate-900 mb-2">ATS Summary</h4>
              <p className="text-sm text-slate-700 leading-relaxed">{analysis.atsSummary}</p>
            </div>

            {analysis.formattingIssues.length > 0 && (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="text-sm font-semibold text-slate-900 mb-2">Formatting issues</h4>
                <ul className="text-sm text-slate-700 space-y-1 list-disc list-inside">
                  {analysis.formattingIssues.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {analysis.keywordSuggestions.length > 0 && (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="text-sm font-semibold text-slate-900 mb-2">Keyword suggestions</h4>
                <div className="flex flex-wrap gap-2">
                  {analysis.keywordSuggestions.map((kw) => (
                    <Badge key={kw} variant="secondary" className="text-xs">
                      {kw}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {analysis.improvements.length > 0 && (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Improvement feedback
                </h4>
                <div className="space-y-3">
                  {analysis.improvements.map((item, i) => (
                    <div key={`${item.area}-${i}`} className="border-l-2 border-orange-200 pl-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-slate-900">{item.area}</span>
                        <Badge className={`text-xs ${severityColor[item.severity]}`}>
                          {item.severity}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">{item.suggestion}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {analysis.recommendedSkills.length > 0 && (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Skills to develop
                </h4>
                <div className="space-y-2">
                  {analysis.recommendedSkills.map((item) => (
                    <div key={item.skill} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3">
                      <div className="flex items-center gap-2 min-w-[140px]">
                        <span className="text-sm font-medium text-slate-900">{item.skill}</span>
                        <Badge className={`text-xs ${priorityColor[item.priority]}`}>
                          {item.priority}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">{item.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {analysis.recommendedCertifications.length > 0 && (
              <div className="p-4 bg-white rounded-lg border border-slate-200">
                <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Certifications & courses
                </h4>
                <div className="space-y-3">
                  {analysis.recommendedCertifications.map((item) => (
                    <div key={item.name}>
                      <p className="text-sm font-medium text-slate-900">
                        {item.name}
                        {item.provider && (
                          <span className="text-slate-500 font-normal"> · {item.provider}</span>
                        )}
                      </p>
                      <p className="text-sm text-slate-600">{item.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {analysis.jdGap && (
              <div className="p-4 bg-white rounded-lg border border-indigo-200">
                <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                  Job description gap analysis
                </h4>
                <p className="text-sm text-slate-700 mb-3">{analysis.jdGap.gapSummary}</p>

                {analysis.jdGap.matchedRequirements.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-green-700 mb-1">Matched</p>
                    <ul className="text-sm text-slate-600 space-y-0.5 list-disc list-inside">
                      {analysis.jdGap.matchedRequirements.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.jdGap.missingRequirements.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-red-700 mb-1">Gaps</p>
                    <ul className="text-sm text-slate-600 space-y-0.5 list-disc list-inside">
                      {analysis.jdGap.missingRequirements.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.jdGap.tailoringSuggestions.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-indigo-700 mb-1">Tailoring tips</p>
                    <ul className="text-sm text-slate-600 space-y-0.5 list-disc list-inside">
                      {analysis.jdGap.tailoringSuggestions.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ResumeInsightsPanel;
